import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthProvider";
import { useUserStore } from "../store/user.store";
import { doc, updateDoc } from "firebase/firestore";
import { db } from "../lib/firebase";
import { Button } from "../components/common/Button";
import { Input } from "../components/common/Input";
import { toast } from "react-hot-toast";
import { AddMoneyModal } from "../components/wallet/AddMoneyModal";
import { CommunicationChannel } from "../types/index";
import {
  Building,
  MapPin,
  Edit2,
  Save,
  Phone,
  Mail,
  Package,
  ShieldCheck,
  ChevronRight,
  Leaf,
  MessageCircle,
  Utensils,
} from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useLocationStore } from "../store/location.store";
import { getUserProfile } from "../services/user.service";
import { useLiveWallet } from "../hooks/useLiveWallet";
import { setMyWhatsAppOptIn } from "../services/whatsappBroadcast.service";

const normalizePhone = (phone: string): string => {
  if (!phone) return "";
  let cleaned = phone.replace(/\s/g, "");
  if (cleaned.startsWith("+91")) cleaned = cleaned.substring(3);
  if (cleaned.startsWith("91") && cleaned.length === 12) cleaned = cleaned.substring(2);
  cleaned = cleaned.replace(/\D/g, "");
  return cleaned.length === 10 ? `+91${cleaned}` : phone;
};

const Profile: React.FC = () => {
  const { user } = useAuth();
  const { userProfile, setUserProfile } = useUserStore();
  const location = useLocation();
  const navigate = useNavigate();
  const locLabel = useLocationStore((s) => s.label);
  const isCommunityLoc = useLocationStore((s) => s.isCommunity);
  const communityName = useLocationStore((s) => s.communityName);
  const setPromptOpen = useLocationStore((s) => s.setPromptOpen);
  const isPromptOpen = useLocationStore((s) => s.isPromptOpen);

  const [isEditing, setIsEditing] = useState(false);
  const [waOptIn, setWaOptIn] = useState<boolean>(false);
  const [waSaving, setWaSaving] = useState(false);
  useEffect(() => {
    setWaOptIn(userProfile?.whatsappOptIn === true);
  }, [userProfile?.whatsappOptIn]);

  const toggleWaOptIn = async () => {
    const next = !waOptIn;
    setWaSaving(true);
    try {
      await setMyWhatsAppOptIn(next);
      setWaOptIn(next);
      if (setUserProfile && userProfile) setUserProfile({ ...userProfile, whatsappOptIn: next });
      toast.success(next ? "You'll get kitchen specials on WhatsApp" : "Unsubscribed from WhatsApp offers");
    } catch (e) {
      console.error(e);
      toast.error("Couldn't update. Try again.");
    } finally {
      setWaSaving(false);
    }
  };
  const [isLoading, setIsLoading] = useState(false);
  const [showAddMoney, setShowAddMoney] = useState(false);
  // Live balance from wallets/{uid} (single source of truth; users.walletBalance is a mirror)
  const { balance: walletBalance } = useLiveWallet(user?.uid, userProfile?.walletBalance);

  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    bio: "",
    officeAddress: "",
    communicationChannels: ["whatsapp"] as CommunicationChannel[],
    isVeg: false,
  });

  const refreshProfile = useCallback(async () => {
    if (!user?.uid) return;
    try {
      const fresh = await getUserProfile(user.uid);
      if (fresh && setUserProfile) setUserProfile(fresh);

      const profile = fresh || userProfile;
      if (profile) {
        setFormData({
          name: profile.name || "",
          phone: normalizePhone(profile.phone || ""),
          email: profile.email || "",
          bio: profile.bio || "",
          officeAddress: profile.officeAddress || "",
          communicationChannels: profile.communicationChannels || ["whatsapp"],
          isVeg: profile.preferences?.dietary_restrictions?.includes("veg") || false,
        });
      }
    } catch (err) {
      console.error("Profile load error", err);
    }
  }, [user?.uid, userProfile, setUserProfile]);

  // PayU return: /profile?payu=success|pending|failed|tampered|error&txnid=...
  // The wallet is credited server-side (surl/webhook/reconciler); we just show the outcome
  // and wait for the live wallet listener / intent doc to reflect it.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const payu = params.get("payu");
    const txnid = params.get("txnid");
    if (!payu) return;
    navigate(location.pathname, { replace: true });
    if (payu === "success") {
      toast.success("Payment successful — your wallet has been topped up.");
    } else if (payu === "pending") {
      toast("Payment is pending with the bank. We'll credit your wallet automatically once PayU confirms (usually within 15 minutes).", { icon: "⏳", duration: 8000 });
    } else if (payu === "failed") {
      toast.error("Payment failed or was cancelled. No money was added.");
    } else {
      toast.error(`We couldn't verify this payment${txnid ? ` (ref ${txnid})` : ""}. If money was debited it will be reconciled automatically.`, { duration: 8000 });
    }
    refreshProfile();
  }, [location, navigate, refreshProfile]);

  useEffect(() => {
    if (!user?.uid) return;
    refreshProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid]);

  const handleSave = async () => {
    if (!user?.uid) return;
    setIsLoading(true);
    try {
      const updates = {
        name: formData.name.trim(),
        email: formData.email.trim(),
        bio: formData.bio.trim(),
        officeAddress: formData.officeAddress.trim(),
        communicationChannels: formData.communicationChannels,
        preferences: {
          ...userProfile?.preferences,
          dietary_restrictions: formData.isVeg ? ["veg"] : [],
        },
      };
      await updateDoc(doc(db, "users", user.uid), updates);
      if (setUserProfile && userProfile) {
        setUserProfile({ ...userProfile, ...updates });
      }
      toast.success("Saved");
      setIsEditing(false);
    } catch (error) {
      console.error(error);
      toast.error("Couldn’t save. Try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const openLocationEditor = () => {
    setPromptOpen(true);
  };

  // Refresh address when the global location modal closes
  const wasPromptOpen = React.useRef(false);
  useEffect(() => {
    if (wasPromptOpen.current && !isPromptOpen) {
      refreshProfile();
    }
    wasPromptOpen.current = isPromptOpen;
  }, [isPromptOpen, refreshProfile]);

  const meta = userProfile?.homeAddressMeta;
  const isCommunity = meta?.mode === "community" || isCommunityLoc;
  const displayAddress =
    userProfile?.homeAddress ||
    userProfile?.address ||
    locLabel ||
    "";
  const displayCommunity =
    meta?.communityName || communityName || "Bren Northern Lights";

  if (!user) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-28">
      <div className="max-w-lg mx-auto px-4 pt-4 sm:pt-8 space-y-4">
        {/* Compact identity */}
        <section className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 flex items-center gap-3">
          <img
            src={`https://ui-avatars.com/api/?name=${encodeURIComponent(
              formData.name || "R"
            )}&background=ffedd5&color=ea580c&size=96&bold=true`}
            alt=""
            className="w-14 h-14 rounded-full border-2 border-orange-100 flex-shrink-0"
          />
          <div className="min-w-0 flex-1">
            <h1 className="text-lg font-bold text-gray-900 truncate">
              {formData.name || "Your profile"}
            </h1>
            <p className="text-sm text-gray-500 flex items-center gap-1.5 truncate">
              <Phone size={14} className="text-green-600 flex-shrink-0" />
              {formData.phone || "Phone not set"}
            </p>
          </div>
          {!isEditing ? (
            <button
              type="button"
              onClick={() => setIsEditing(true)}
              className="flex-shrink-0 inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-3 py-2 text-sm font-semibold text-gray-700 active:bg-gray-200"
            >
              <Edit2 size={14} /> Edit
            </button>
          ) : (
            <button
              type="button"
              onClick={() => {
                setIsEditing(false);
                refreshProfile();
              }}
              className="flex-shrink-0 rounded-full bg-red-50 px-3 py-2 text-sm font-semibold text-red-600"
            >
              Cancel
            </button>
          )}
        </section>

        {/* Quick links */}
        <section className="grid grid-cols-2 gap-3">
          <Link
            to="/orders"
            className="flex items-center gap-3 rounded-2xl border border-gray-100 bg-white p-4 shadow-sm active:bg-orange-50"
          >
            <div className="rounded-xl bg-orange-100 p-2.5 text-orange-600">
              <Package size={20} />
            </div>
            <div className="min-w-0">
              <p className="font-bold text-gray-900 text-sm">My Orders</p>
              <p className="text-xs text-gray-500">Track & history</p>
            </div>
          </Link>
          <Link
            to="/my-tiffin"
            className="flex items-center gap-3 rounded-2xl border border-gray-100 bg-white p-4 shadow-sm active:bg-orange-50"
          >
            <div className="rounded-xl bg-orange-100 p-2.5 text-orange-600">
              <Utensils size={20} />
            </div>
            <div className="min-w-0">
              <p className="font-bold text-gray-900 text-sm">My subscription</p>
              <p className="text-xs text-gray-500">Pause, skip & plans</p>
            </div>
          </Link>
          <button
            type="button"
            onClick={() => setShowAddMoney(true)}
            className="flex items-center gap-3 rounded-2xl border border-gray-100 bg-white p-4 shadow-sm text-left active:bg-orange-50 col-span-2 sm:col-span-1"
          >
            <div className="rounded-xl bg-emerald-100 p-2.5 text-emerald-700">
              <ShieldCheck size={20} />
            </div>
            <div className="min-w-0">
              <p className="font-bold text-gray-900 text-sm">
                ₹{walletBalance.toFixed(0)}
              </p>
              <p className="text-xs text-gray-500">Add money</p>
            </div>
          </button>
        </section>

        {/* Delivery address — primary */}
        <section className="rounded-2xl border border-orange-100 bg-white shadow-sm overflow-hidden">
          <div className="flex items-center justify-between gap-2 px-4 pt-4 pb-2">
            <h2 className="font-bold text-gray-900 flex items-center gap-2">
              {isCommunity ? (
                <Building className="text-orange-600" size={18} />
              ) : (
                <MapPin className="text-orange-600" size={18} />
              )}
              Delivery address
            </h2>
            <button
              type="button"
              onClick={openLocationEditor}
              className="text-sm font-bold text-orange-600 bg-orange-50 px-3 py-2 rounded-full border border-orange-100 min-h-[40px]"
            >
              {displayAddress ? "Change" : "Add"}
            </button>
          </div>

          <div className="px-4 pb-4">
            {displayAddress ? (
              <div className="rounded-xl bg-orange-50/80 border border-orange-100 p-3">
                {isCommunity && (
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <span className="text-xs font-bold uppercase tracking-wide text-orange-700 bg-white px-2 py-1 rounded-md border border-orange-100">
                      {displayCommunity}
                    </span>
                    {(meta?.block || meta?.flat || meta?.houseNumber) && (
                      <span className="text-sm font-bold text-orange-900">
                        {[meta?.block, meta?.flat || meta?.houseNumber]
                          .filter(Boolean)
                          .join(" · ")}
                      </span>
                    )}
                  </div>
                )}
                <p className="text-sm text-gray-800 leading-relaxed break-words">
                  {displayAddress}
                </p>
                {isCommunity && (
                  <p className="text-xs text-green-700 font-medium mt-2">
                    Community delivery rates apply here
                  </p>
                )}
              </div>
            ) : (
              <button
                type="button"
                onClick={openLocationEditor}
                className="w-full rounded-xl border-2 border-dashed border-orange-200 bg-orange-50/40 py-8 px-4 text-center active:bg-orange-50"
              >
                <MapPin className="mx-auto mb-2 text-orange-400" size={28} />
                <p className="font-bold text-gray-800 text-sm">
                  Add where we should deliver
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Bren Northern Lights flats get lower delivery fees
                </p>
              </button>
            )}
          </div>
        </section>

        {/* Preferences */}
        <section className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm space-y-4">
          <h2 className="font-bold text-gray-900 text-sm">Preferences</h2>

          <button
            type="button"
            disabled={!isEditing}
            onClick={() =>
              isEditing && setFormData((p) => ({ ...p, isVeg: !p.isVeg }))
            }
            className={`w-full flex items-center justify-between rounded-xl border px-3 py-3 text-left ${
              formData.isVeg
                ? "border-green-200 bg-green-50"
                : "border-gray-100 bg-gray-50"
            } ${!isEditing ? "opacity-90" : "active:scale-[0.99]"}`}
          >
            <span className="flex items-center gap-2 font-semibold text-sm text-gray-800">
              <Leaf
                size={18}
                className={formData.isVeg ? "text-green-600" : "text-gray-400"}
              />
              Vegetarian only
            </span>
            <span
              className={`text-xs font-bold px-2 py-1 rounded-full ${
                formData.isVeg
                  ? "bg-green-600 text-white"
                  : "bg-gray-200 text-gray-600"
              }`}
            >
              {formData.isVeg ? "On" : "Off"}
            </span>
          </button>

          <div>
            <p className="text-xs font-bold text-gray-400 uppercase mb-2 flex items-center gap-1">
              <MessageCircle size={12} /> Order updates
            </p>
            <div className="flex flex-wrap gap-2">
              {(["whatsapp", "sms"] as CommunicationChannel[]).map((ch) => {
                const on = formData.communicationChannels.includes(ch);
                return (
                  <button
                    key={ch}
                    type="button"
                    disabled={!isEditing}
                    onClick={() => {
                      if (!isEditing) return;
                      setFormData((prev) => {
                        const cur = prev.communicationChannels;
                        return {
                          ...prev,
                          communicationChannels: on
                            ? cur.filter((c) => c !== ch)
                            : [...cur, ch],
                        };
                      });
                    }}
                    className={`px-3 py-2 rounded-full text-sm font-semibold border min-h-[40px] ${
                      on
                        ? "bg-orange-500 text-white border-orange-500"
                        : "bg-white text-gray-600 border-gray-200"
                    }`}
                  >
                    {ch === "whatsapp" ? "WhatsApp" : "SMS"}
                  </button>
                );
              })}
            </div>
          </div>

          <button
            type="button"
            onClick={toggleWaOptIn}
            disabled={waSaving}
            className="w-full flex items-center justify-between rounded-xl border border-gray-100 px-3 py-3 text-left active:scale-[0.99] disabled:opacity-60"
          >
            <span className="flex flex-col">
              <span className="flex items-center gap-2 font-semibold text-sm text-gray-800">
                <MessageCircle size={18} className={waOptIn ? "text-green-600" : "text-gray-400"} />
                Kitchen specials on WhatsApp
              </span>
              <span className="text-[11px] text-gray-500 ml-7">
                Daily specials &amp; offers from Ruchira kitchens. Reply STOP anytime.
              </span>
            </span>
            <span
              className={`text-xs font-bold px-2 py-1 rounded-full ${
                waOptIn ? "bg-green-600 text-white" : "bg-gray-200 text-gray-600"
              }`}
            >
              {waSaving ? "…" : waOptIn ? "On" : "Off"}
            </span>
          </button>
        </section>

        {/* Personal details */}
        <section className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm space-y-4">
          <h2 className="font-bold text-gray-900 text-sm">Personal details</h2>

          <div>
            <label className="text-xs font-bold text-gray-400 uppercase mb-1 block">
              Name
            </label>
            {isEditing ? (
              <Input
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                placeholder="Your name"
              />
            ) : (
              <p className="text-sm font-medium text-gray-800 py-1">
                {formData.name || "—"}
              </p>
            )}
          </div>

          <div>
            <label className="text-xs font-bold text-gray-400 uppercase mb-1 block">
              Email
            </label>
            {isEditing ? (
              <Input
                type="email"
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
                placeholder="you@email.com"
              />
            ) : (
              <p className="text-sm font-medium text-gray-800 py-1 flex items-center gap-2">
                <Mail size={14} className="text-blue-500" />
                {formData.email || "Not set"}
              </p>
            )}
          </div>

          <div>
            <label className="text-xs font-bold text-gray-400 uppercase mb-1 block">
              Delivery notes
            </label>
            {isEditing ? (
              <textarea
                rows={3}
                className="w-full border border-gray-200 p-3 rounded-xl text-sm outline-none focus:border-orange-500"
                value={formData.bio}
                onChange={(e) =>
                  setFormData({ ...formData, bio: e.target.value })
                }
                placeholder="Gate code, leave with security, call on arrival…"
              />
            ) : (
              <p className="text-sm text-gray-600 py-1">
                {formData.bio || "No special notes"}
              </p>
            )}
          </div>

          <div>
            <label className="text-xs font-bold text-gray-400 uppercase mb-1 block">
              Work address (optional)
            </label>
            {isEditing ? (
              <textarea
                rows={2}
                className="w-full border border-gray-200 p-3 rounded-xl text-sm outline-none focus:border-orange-500"
                value={formData.officeAddress}
                onChange={(e) =>
                  setFormData({ ...formData, officeAddress: e.target.value })
                }
                placeholder="Office / alternate address"
              />
            ) : (
              <p className="text-sm text-gray-600 py-1">
                {formData.officeAddress || "Not set"}
              </p>
            )}
          </div>
        </section>

        {/* Wallet detail */}
        <section className="rounded-2xl bg-gradient-to-br from-gray-900 to-gray-800 text-white p-4 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="text-xs text-gray-400 font-bold uppercase tracking-wide">
                Wallet
              </p>
              <p className="text-3xl font-bold font-mono mt-1">
                ₹{walletBalance.toFixed(2)}
              </p>
            </div>
            <ShieldCheck className="text-green-400" size={28} />
          </div>
          <button
            type="button"
            onClick={() => setShowAddMoney(true)}
            className="w-full rounded-xl bg-white text-gray-900 font-bold py-3 text-sm active:bg-gray-100"
          >
            Add funds
          </button>
        </section>

        <Link
          to="/orders"
          className="flex items-center justify-between rounded-2xl border border-gray-100 bg-white px-4 py-3.5 shadow-sm text-sm font-semibold text-gray-800"
        >
          View all orders
          <ChevronRight size={18} className="text-gray-400" />
        </Link>
      </div>

      {/* Sticky save when editing */}
      {isEditing && (
        <div className="fixed bottom-0 inset-x-0 z-40 border-t border-gray-200 bg-white/95 backdrop-blur px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
          <div className="max-w-lg mx-auto">
            <Button
              onClick={handleSave}
              isLoading={isLoading}
              className="w-full py-3 text-base font-bold"
            >
              <Save size={16} className="mr-2" /> Save changes
            </Button>
          </div>
        </div>
      )}



      {showAddMoney && (
        <AddMoneyModal
          purpose="wallet_recharge"
          returnTo="profile"
          onClose={() => setShowAddMoney(false)}
          onSuccess={() => {
            setShowAddMoney(false);
            refreshProfile();
          }}
        />
      )}
    </div>
  );
};

export default Profile;
